"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildPagestateFingerprints = exports.buildMobileFingerprint = void 0;
const ancestry_1 = require("./ancestry");
const disambiguation_1 = require("./disambiguation");
const enrichment_1 = require("./enrichment");
const normalizer_1 = require("./normalizer");
const selector_1 = require("./selector");
const signature_1 = require("./signature");
/**
 * The rule-agnostic element signature for one leaf: its own selector, its
 * siblings and children descriptors, and its filtered ancestry chain,
 * assembled into one deterministic string.
 */
const buildMobileFingerprint = ({ tree, leafId, config, caches }) => {
    const leaf = tree.getView(leafId);
    if (!leaf) {
        return '';
    }
    const normalizer = (0, normalizer_1.buildFieldNormalizer)();
    let element = (0, selector_1.generateViewSelector)(leaf, selector_1.ViewSelectorConfig.full(), normalizer);
    element = (0, signature_1.stripLabelNumericSuffixFromSelector)(element);
    const siblings = (0, enrichment_1.siblingsSummary)(tree, leafId, config, normalizer, caches?.siblingsCache);
    const childEnrichFn = (vid) => {
        const cached = caches?.childDescriptorCache?.get(vid);
        if (cached !== undefined) {
            return cached;
        }
        const desc = (0, enrichment_1.renderDescriptor)(tree.getChildren(vid), config, normalizer);
        // Only re-include a dropped generic ancestor when it carries listed
        // (informative) text.
        const value = desc.includes('short:[') ? desc : '';
        caches?.childDescriptorCache?.set(vid, value);
        return value;
    };
    const ancestry = (0, ancestry_1.walkGenericOnly)({
        viewId: leafId,
        tree,
        maxDepth: config.ancestryMaxDepth,
        selectorConfig: selector_1.ViewSelectorConfig.ancestry(),
        normalizer,
        childEnrichFn
    });
    const children = (0, enrichment_1.childrenSummary)(tree, leafId, config, normalizer);
    return (0, signature_1.buildSignatureText)({ element, siblings, ancestry, children });
};
exports.buildMobileFingerprint = buildMobileFingerprint;
/**
 * Final element signature per `iid` for every case in ONE page state.
 *
 * The result does not depend on the order `cases` are supplied in, nor on which
 * views happened to fail: a colliding group's positional suffix ranks every
 * view in `tree.viewIds` carrying that signature, not just the failing ones.
 */
const buildPagestateFingerprints = (tree, cases, config) => {
    // `sigCache` is keyed on `axeViewId` alone: cases are keyed by
    // `(axeViewId, ruleId)` but `buildMobileFingerprint` is rule-agnostic, so a
    // node failing K rules would otherwise recompute one signature K times.
    const caches = {
        siblingsCache: new Map(),
        childDescriptorCache: new Map()
    };
    const sigCache = new Map();
    const sigFor = (id) => {
        const hit = sigCache.get(id);
        if (hit !== undefined) {
            return hit;
        }
        const v = (0, exports.buildMobileFingerprint)({ tree, leafId: id, config, caches });
        sigCache.set(id, v);
        return v;
    };
    const viewOrder = new Map(tree.viewIds.map((id, index) => [id, index]));
    const records = cases.map(c => ({
        iid: c.iid,
        sessionId: c.sessionId ?? null,
        axeViewId: c.axeViewId,
        signatureText: sigFor(c.axeViewId),
        viewOrder: viewOrder.get(c.axeViewId) ?? -1
    }));
    if (config.disambiguation === 'off') {
        const out = {};
        for (const r of records) {
            out[r.iid] = r.signatureText;
        }
        return out;
    }
    let peerIndex = null;
    const peersOf = (signatureText) => {
        if (peerIndex === null) {
            peerIndex = new Map();
            for (const id of tree.viewIds) {
                const sig = sigFor(id);
                if (sig === '') {
                    continue;
                }
                const bucket = peerIndex.get(sig);
                if (bucket) {
                    bucket.push(id);
                }
                else {
                    peerIndex.set(sig, [id]);
                }
            }
        }
        return peerIndex.get(signatureText);
    };
    return (0, disambiguation_1.disambiguate)(records, config.disambiguation, peersOf);
};
exports.buildPagestateFingerprints = buildPagestateFingerprints;
//# sourceMappingURL=fingerprint.js.map